import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FaArrowLeft } from 'react-icons/fa';
import conciliacionBancariaService from '../../services/conciliacionBancariaService';
import '../../styles/pages/CargaCompras.css';
import '../../styles/pages/ConciliacionBancaria.css';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const ANIO_ACTUAL = new Date().getFullYear();
const ANIOS = [2024, 2025, 2026];

const fmt = (n) =>
  Number(n || 0).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });

const fmtDate = (ts) =>
  ts ? new Date(ts).toLocaleDateString('es-CL') : '';

const ESTADO_INICIAL = Array(12).fill(null).map((_, i) => ({
  mes: i + 1, cargado: false, fecha_carga: null, origen: null,
}));

const RESUMEN_INICIAL = Array(12).fill(null).map((_, i) => ({
  mes: i + 1, total_movimientos: 0, total_cargos: 0, total_abonos: 0,
  pendientes: 0, conciliados: 0,
}));

const CartolasPage = ({ token, id_company }) => {
  const navigate = useNavigate();
  const fileRefs = useRef(Array(12).fill(null).map(() => ({ current: null })));

  const [cuentas, setCuentas] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState('');
  const [anio, setAnio] = useState(ANIO_ACTUAL);

  const [resumen, setResumen] = useState(RESUMEN_INICIAL);
  const [estado, setEstado] = useState(ESTADO_INICIAL);

  const [files, setFiles] = useState(Array(12).fill(null));
  const [loadingImport, setLoadingImport] = useState(Array(12).fill(false));
  const [loadingApi, setLoadingApi] = useState(Array(12).fill(false));

  // ── Cargar cuentas ────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      conciliacionBancariaService.getCuentas(id_company, token),
      conciliacionBancariaService.getBancos(token),
    ]).then(([cuentasData, bancosData]) => {
      const activas = cuentasData.filter(c => c.activo);
      setCuentas(activas);
      setBancos(bancosData);
      if (activas.length === 1) setCuentaSeleccionada(String(activas[0].id));
    }).catch(() => {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudieron cargar las cuentas bancarias' });
    });
  }, [id_company, token]);

  // ── Cargar resumen y estado de carga al cambiar cuenta o año ─────────────
  const cargarDatos = useCallback(async () => {
    if (!cuentaSeleccionada) {
      setResumen(RESUMEN_INICIAL);
      setEstado(ESTADO_INICIAL);
      return;
    }
    try {
      const [resumenData, estadoData] = await Promise.all([
        conciliacionBancariaService.getResumenAnual(cuentaSeleccionada, anio, token),
        conciliacionBancariaService.getEstadoCarga(cuentaSeleccionada, anio, token),
      ]);
      setResumen(resumenData);
      setEstado(estadoData);
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo cargar el estado de cartolas' });
    }
  }, [cuentaSeleccionada, anio, token]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  // ── File change ───────────────────────────────────────────────────────────
  const handleFileChange = (index, file) => {
    const newFiles = [...files];
    newFiles[index] = file;
    setFiles(newFiles);
  };

  // ── Importar Excel por mes ────────────────────────────────────────────────
  const handleImportar = async (index) => {
    const file = files[index];
    if (!file) {
      Swal.fire({ icon: 'warning', title: 'Selecciona un archivo', text: 'Elige un archivo Excel o CSV primero' });
      return;
    }

    if (estado[index]?.cargado) {
      Swal.fire({ icon: 'warning', title: 'Período ya cargado', text: 'Use "Limpiar" para reemplazar los datos.' });
      return;
    }

    const setLoading = (v) => setLoadingImport(prev => { const a = [...prev]; a[index] = v; return a; });
    setLoading(true);

    Swal.fire({
      title: 'Importando cartola...',
      html: `Procesando <b>${file.name}</b> — ${MESES[index]} ${anio}`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const result = await conciliacionBancariaService.importarExcel(
        file, cuentaSeleccionada, id_company, index + 1, anio, token
      );

      const newFiles = [...files];
      newFiles[index] = null;
      setFiles(newFiles);
      if (fileRefs.current[index]?.current) fileRefs.current[index].current.value = '';

      const omitidos = result.meses_omitidos?.length
        ? `<br/><small style="color:#888">${result.meses_omitidos.length} mes(es) omitidos por ya estar cargados</small>`
        : '';
      Swal.fire({
        icon: 'success',
        title: 'Importación completada',
        html: `<b>${result.insertados}</b> movimientos en <b>${result.meses_cargados?.length || 1}</b> mes(es)${omitidos}`,
      });
      cargarDatos();
    } catch (err) {
      const msg = err.response?.data?.error || 'No se pudo procesar el archivo. Verifica el formato.';
      Swal.fire({ icon: 'error', title: 'Error al importar', text: msg });
    } finally {
      setLoading(false);
    }
  };

  // ── Consultar API banco ───────────────────────────────────────────────────
  const handleConsultarApi = async (index) => {
    if (estado[index]?.cargado) {
      Swal.fire({ icon: 'warning', title: 'Período ya cargado', text: 'Use "Limpiar" para reemplazar los datos.' });
      return;
    }

    const setLoading = (v) => setLoadingApi(prev => { const a = [...prev]; a[index] = v; return a; });
    setLoading(true);

    Swal.fire({
      title: 'Consultando banco...',
      html: `Obteniendo movimientos de <b>${MESES[index]} ${anio}</b>`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const result = await conciliacionBancariaService.consultarApi({
        id_cuenta_banco: cuentaSeleccionada, id_company,
        mes: index + 1, anio,
      }, token);
      Swal.fire({ icon: 'success', title: 'Sincronización completada', text: result.message });
      cargarDatos();
    } catch (err) {
      Swal.fire({
        icon: 'info',
        title: 'Integración no disponible',
        text: err.response?.data?.error || 'Integración directa no disponible para este banco.',
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Limpiar mes ───────────────────────────────────────────────────────────
  const handleLimpiar = async (index) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: `¿Limpiar ${MESES[index]} ${anio}?`,
      text: 'Se eliminarán todos los movimientos del período. Esta acción no se puede deshacer.',
      showCancelButton: true,
      confirmButtonColor: '#e53e3e',
      confirmButtonText: 'Sí, limpiar',
      cancelButtonText: 'Cancelar',
    });
    if (!result.isConfirmed) return;

    try {
      await conciliacionBancariaService.limpiarPeriodo(cuentaSeleccionada, index + 1, anio, token);
      Swal.fire({ icon: 'success', title: 'Período limpiado', timer: 1300, showConfirmButton: false });
      cargarDatos();
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo limpiar el período' });
    }
  };

  const getBancoLabel = (id) => {
    const cuenta = cuentas.find(c => String(c.id) === String(id));
    if (!cuenta) return '';
    const banco = bancos.find(b => b.value === cuenta.banco);
    return banco ? banco.label : cuenta.banco;
  };

  const cuentaInfo = cuentas.find(c => String(c.id) === String(cuentaSeleccionada));
  const tieneIntegracion = !!cuentaInfo?.integracion_tipo;

  const mesConsultando = loadingApi.findIndex(v => v);
  const mesImportando  = loadingImport.findIndex(v => v);

  return (
    <div className="carga-compras">

      {/* ── Overlay de carga ── */}
      {(loadingApi.some(v => v) || loadingImport.some(v => v)) && (
        <div className="sii-loading-overlay">
          <div className="sii-loading-modal">
            <div className="sii-loading-spinner" />
            <h3>
              {loadingApi.some(v => v) ? 'Consultando banco...' : 'Importando cartola...'}
            </h3>
            {mesConsultando >= 0 && <p>{MESES[mesConsultando]} {anio}</p>}
            {mesImportando  >= 0 && <p>{MESES[mesImportando]} {anio}</p>}
            <small>Por favor espere, no cierre esta ventana</small>
          </div>
        </div>
      )}

      <button className="cb-back-btn" style={{ marginBottom: 16 }} onClick={() => navigate('/conciliacion-bancaria')}>
        <FaArrowLeft /> Volver
      </button>

      <h1>Cartolas Bancarias</h1>

      {/* ── Selectores ── */}
      <div style={{ marginBottom: 20 }}>
        <label>Cuenta bancaria:</label>
        <select
          value={cuentaSeleccionada}
          onChange={e => setCuentaSeleccionada(e.target.value)}
          style={{ marginLeft: 10, padding: 5 }}
        >
          <option value="">-- Seleccionar cuenta --</option>
          {cuentas.map(c => (
            <option key={c.id} value={c.id}>
              {getBancoLabel(c.id)}
              {c.nombre_cuenta ? ` · ${c.nombre_cuenta}` : ''}
              {c.numero_cuenta ? ` (${c.numero_cuenta})` : ''}
            </option>
          ))}
        </select>
        {cuentas.length === 0 && (
          <span
            style={{ marginLeft: 12, fontSize: '0.85rem', color: '#e53e3e', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => navigate('/conciliacion-bancaria/cuentas')}
          >
            No hay cuentas activas — agrega una aquí
          </span>
        )}
      </div>

      <div style={{ marginBottom: 20 }}>
        <label>Año:</label>
        <select
          value={anio}
          onChange={e => setAnio(Number(e.target.value))}
          style={{ marginLeft: 10, padding: 5 }}
        >
          {ANIOS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {/* ── Tabla de 12 meses ── */}
      <table>
        <thead>
          <tr>
            <th>Mes</th>
            <th>Estado</th>
            <th>Archivo Excel / CSV</th>
            <th>Importar</th>
            <th>Movimientos</th>
            <th>Total Cargos</th>
            <th>Total Abonos</th>
            <th>Consultar Banco</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {MESES.map((nombreMes, index) => {
            const fila   = resumen[index];
            const ctrl   = estado[index];
            const cargado = ctrl?.cargado;
            const hayMovimientos = fila.total_movimientos > 0;

            const fileRef = { current: null };
            fileRefs.current[index] = fileRef;

            return (
              <tr key={nombreMes}>
                <td><strong>{nombreMes}</strong></td>

                {/* Estado de carga */}
                <td>
                  {cargado ? (
                    <span style={{ color: '#2d7a2d', fontSize: '0.8rem', fontWeight: 600 }}>
                      ✓ {ctrl.origen === 'rasca' ? 'Rasca' : 'Excel'}<br />
                      <span style={{ fontWeight: 400, color: '#555' }}>{fmtDate(ctrl.fecha_carga)}</span>
                    </span>
                  ) : (
                    <span style={{ color: '#999', fontSize: '0.8rem' }}>Sin cargar</span>
                  )}
                </td>

                <td>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    ref={el => { fileRefs.current[index] = { current: el }; }}
                    disabled={!cuentaSeleccionada || cargado || loadingImport[index]}
                    onChange={e => handleFileChange(index, e.target.files[0])}
                  />
                </td>

                <td>
                  <button
                    onClick={() => handleImportar(index)}
                    disabled={!cuentaSeleccionada || !files[index] || cargado || loadingImport[index]}
                  >
                    {loadingImport[index] ? 'Importando...' : 'Importar'}
                  </button>
                </td>

                <td className={hayMovimientos ? 'status-true' : ''}>
                  {hayMovimientos ? `${fila.total_movimientos} mov.` : 'Sin datos'}
                </td>

                <td className={hayMovimientos ? 'status-true' : ''}>
                  {hayMovimientos ? fmt(fila.total_cargos) : '-'}
                </td>

                <td className={hayMovimientos ? 'status-true' : ''}>
                  {hayMovimientos ? fmt(fila.total_abonos) : '-'}
                </td>

                <td>
                  <button
                    onClick={() => handleConsultarApi(index)}
                    disabled={!cuentaSeleccionada || cargado || loadingApi[index]}
                    title={!tieneIntegracion ? 'Configura credenciales de integración en Cuentas Bancarias' : ''}
                  >
                    {loadingApi[index] ? 'Consultando...' : `Consultar ${getBancoLabel(cuentaSeleccionada) || 'Banco'}`}
                  </button>
                </td>

                <td>
                  {cargado && (
                    <button
                      onClick={() => handleLimpiar(index)}
                      disabled={!cuentaSeleccionada}
                      style={{ background: '#fff5f5', color: '#e53e3e', border: '1px solid #e53e3e' }}
                    >
                      Limpiar
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CartolasPage;
